/* antd */
import { message } from 'antd';
import { initializeApp } from 'firebase/app';
import { FacebookAuthProvider, getAuth, GoogleAuthProvider, OAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
/* Dependencies */
import jwt_decode from 'jwt-decode';
import React, { createContext, useCallback, useContext, useState } from 'react';

import { IUserData, IUserProfile, SignInCredentials } from '../interfaces/auth';
/* Services */
import { apiNode, apiPHP } from '../services/api';
import { firebaseConfig } from '../services/firebaseConfig';

/* Interface do Contexto */
interface AuthContextData {
  user: IUserProfile;
  setData: (data: AuthState) => void;
  handleSignIn(credentials: SignInCredentials): Promise<void>;
  logOut(): void;
  signUp(credentials: IUserData): Promise<void>;
  getUserProfile(ID: number): Promise<void>;
  SignInWithGoogle(method: string): void;
  SignInWithFacebook(method: string): void;
  SignInWithApple(method: string): void;
  loadingGoogle: boolean;
  setLoadingGoogle: React.Dispatch<React.SetStateAction<boolean>>;
  loadingFacebook: boolean;
  setLoadingFacebook: React.Dispatch<React.SetStateAction<boolean>>;
  loadingApple: boolean;
  setLoadingApple: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface AuthState {
  token: string;
  user: IUserProfile;
}

interface Plano {
  id: number;
  nome: string;
}

interface UserData {
  foto: string;
  id: number;
  name: string;
  plano: Plano;
}

interface TokenData {
  usr: UserData;
}

interface UserProfile {
  name: string;
  sobrenome: string;
  cidade: null | string;
  descricao: string;
  curtidas_count: number;
  comentarios_count: number;
  jogos_count: number;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

initializeApp(firebaseConfig);

const AuthProvider: React.FC = ({ children }) => {
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingFacebook, setLoadingFacebook] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Portal:token');
    const user = localStorage.getItem('@Portal:user');
    if (token && user) {
      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const getUserProfile = useCallback(async (id: number) => {
    await apiPHP.get(`users/${id}`).then((response) => {
      const {
        cidade,
        comentarios_count,
        curtidas_count,
        descricao,
        jogos_count,
        name,
        sobrenome,
      } = response.data[0];

      let userProfile: UserProfile = {
        name,
        sobrenome,
        cidade,
        descricao,
        curtidas_count,
        comentarios_count,
        jogos_count,
      };

      localStorage.setItem('@Portal:profile', JSON.stringify(userProfile));
    });
  }, []);

  const handleSignIn = useCallback(
    async (data: SignInCredentials, profileUrl?: string | null) => {
      await apiNode
        .post(`login`, {
          login: data.email.trim(),
          password: data.password,
        })
        .then(async (response) => {
          message.success('Login efetuado com sucesso!');

          let token = response.data.token;
          let decoded: TokenData = jwt_decode(token);

          const currentDate = new Date().getTime();
          const expiresAt = new Date(currentDate + (60 * 60 * 24 * 7 * 1000)); // data atual + 1 semana

          let usr: any = {};

          if (!decoded.usr.foto) {
            let profileUrlEdited = profileUrl?.replace('=s96-c', '=s400-c');

            usr = {
              nome: decoded.usr.name,
              email: data.email,
              avatar: profileUrlEdited,
              planoId: decoded.usr.plano.id,
              planoNome: decoded.usr.plano.nome,
              id: decoded.usr.id,
            };
          } else {
            usr = {
              nome: decoded.usr.name,
              email: data.email,
              avatar: decoded.usr.foto,
              planoId: decoded.usr.plano.id,
              planoNome: decoded.usr.plano.nome,
              id: decoded.usr.id,
            };
          }

          let user: IUserProfile = usr;

          localStorage.setItem('@Portal:token', token);
          localStorage.setItem('@Portal:user', JSON.stringify(usr));
          localStorage.setItem('@Portal:tokenExpiresAt', JSON.stringify(expiresAt));
          await getUserProfile(decoded.usr.id);
          setData({ token, user });
        })
        .catch((err) => {
          message.error(err.response.data.msg);
        });
    },
    [getUserProfile]
  );

  const logOut = useCallback(() => {
    localStorage.removeItem('@Portal:token');
    localStorage.removeItem('@Portal:user');
    localStorage.removeItem('@Portal:profile');
    localStorage.removeItem('@Portal:tokenExpiresAt');

    const auth = getAuth();
    signOut(auth)
      .then(() => {
        if (window.location.pathname !== '/') window.location.href = "/"
        else message.success('Logout feito com sucesso, volte sempre!');
      })
      .catch(() => {
        message.error(
          'Ocorreu um erro na tentativa de logout, tente novamente!'
        );
      });

    setData({} as AuthState);
  }, []);

  const signUp = useCallback(
    async (data: IUserData, profileUrl?: string) => {
      await apiPHP
        .post(`register`, {
          name: data.nome,
          sobrenome: data.sobrenome,
          email: data.email.trim(),
          password: data.senha,
          planos_id: 4,
          origens_qrcodes_id: 11,
          celular: data.telefone,
        })
        .then(async () => {
          message.success('Cadastro realizado com sucesso!');
          try {
            await handleSignIn(
              {
                email: data.email,
                password: data.senha,
              },
              profileUrl
            ).then(() => {
              window.location.href = '/';
            });
          } catch (error) {
            message.error(
              'Ocorreu um erro no login automático. Tente fazer manualmente!'
            );
          }
        });
    },
    [handleSignIn]
  );

  const DetachFullName = useCallback((splitName: string[]) => {
    let surname: string = '';
    let name: string = '';

    if (splitName.length === 1) {
      name = String(splitName);
    } else {
      for (let i = 0; i < splitName.length - 1; i++) {
        name += splitName[i];
        name += ' ';
      }

      if (splitName.length > 2) {
        surname = splitName[splitName.length - 1];
      } else if (splitName.length === 2) {
        surname = splitName[1];
      }
    }

    return [name, surname];
  }, []);

  const SignInWithGoogle = useCallback(
    async (method: string) => {
      const provider = new GoogleAuthProvider();
      const auth = getAuth();
      signInWithPopup(auth, provider)
        .then(async (result) => {
          if (!result.user.displayName) {
            throw new Error(
              'Falta a informação de seu nome em sua conta Google!'
            );
          }

          const splitName = result.user.displayName.split(' ');

          const fullName = DetachFullName(splitName);

          if (result.user.email === null) {
            throw new Error('Faltam informações em sua conta Google!');
          } else {
            await signUp(
              {
                nome: fullName[0],
                sobrenome: fullName[1],
                email: result.user.email,
                senha: result.user.uid,
                telefone: result.user.phoneNumber
                  ? result.user.phoneNumber
                  : '35999999999',
              },
              result.user.photoURL ? result.user.photoURL : ''
            ).catch(async () => {
              if (result.user && result.user.email) {
                await handleSignIn(
                  {
                    email: result.user.email,
                    password: result.user.uid,
                  },
                  result.user.photoURL ? result.user.photoURL : ''
                );
              }
            });
          }
          setLoadingGoogle(false);
          if (method === 'register') {
            window.location.href = '/';
          }
        })
        .catch(() => {
          message.error('Falha no login');
          setLoadingGoogle(false);
        });
    },
    [handleSignIn, signUp, DetachFullName]
  );

  const SignInWithFacebook = useCallback(
    (method: string) => {
      const provider = new FacebookAuthProvider();
      const auth = getAuth();

      signInWithPopup(auth, provider)
        .then(async (result) => {
          if (!result.user.displayName) {
            throw new Error(
              'Falta a informação de seu nome em sua conta do Facebook!'
            );
          }

          const splitName = result.user.displayName.split(' ');

          const fullName = DetachFullName(splitName);

          if (result.user.providerData[0].email === null) {
            message.error(
              'Verifique seu e-mail ou deixe-o público para continuar o login com o Facebook!'
            );
          } else {
            await signUp(
              {
                nome: fullName[0],
                sobrenome: fullName[1],
                email: result.user.providerData[0].email,
                senha: result.user.uid,
                telefone: result.user.phoneNumber
                  ? result.user.phoneNumber
                  : '35999999999',
              },
              result.user.photoURL ? result.user.photoURL : ''
            ).catch(async () => {
              if (result.user && result.user.providerData[0].email) {
                await handleSignIn(
                  {
                    email: result.user.providerData[0].email,
                    password: result.user.uid,
                  },
                  result.user.photoURL ? result.user.photoURL : ''
                );
              }
            });
          }
          setLoadingFacebook(false);
          if (method === 'register') {
            window.location.href = '/';
          }
        })
        .catch(() => {
          message.error('Falha no login');
          setLoadingFacebook(false);
        });
    },
    [handleSignIn, signUp, DetachFullName]
  );

  const SignInWithApple = useCallback(
    (method: string) => {
      const provider = new OAuthProvider('apple.com');
      const auth = getAuth();
      signInWithPopup(auth, provider)
        .then(async (result) => {
          if (!result.user.displayName) {
            throw new Error(
              'Falta a informação de seu nome em sua conta Apple!'
            );
          }

          const splitName = result.user.displayName.split(' ');

          const fullName = DetachFullName(splitName);

          if (result.user.email === null) {
            message.error(
              'É necessário que você forneça o e-mail para continuar o login com a Apple!'
            );
          } else {
            await signUp(
              {
                nome: fullName[0],
                sobrenome: fullName[1],
                email: result.user.email,
                senha: result.user.uid,
                telefone: result.user.phoneNumber
                  ? result.user.phoneNumber
                  : '35999999999',
              },
              result.user.photoURL ? result.user.photoURL : ''
            ).catch(async () => {
              if (result.user && result.user.email) {
                await handleSignIn(
                  {
                    email: result.user.email,
                    password: result.user.uid,
                  },
                  result.user.photoURL ? result.user.photoURL : ''
                );
              }
            });
          }
          setLoadingApple(false);
          if (method === 'register') {
            window.location.href = '/';
          }
        })
        .catch(() => {
          message.error('Falha no login');
          setLoadingApple(false);
        });
    },
    [handleSignIn, signUp, DetachFullName]
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        setData,
        handleSignIn,
        logOut,
        signUp,
        getUserProfile,
        SignInWithGoogle,
        SignInWithFacebook,
        SignInWithApple,
        loadingGoogle,
        setLoadingGoogle,
        loadingFacebook,
        setLoadingFacebook,
        loadingApple,
        setLoadingApple,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
